import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import WithTooltip from 'components/shared/WithTooltip'
import styles from 'components/answers/answer-controls.module.scss'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import { Camera, Video } from 'lucide-react'
import {
    hasNativeCameraPermissionGate,
    hasNativeVideoCaptureInput,
    isRunningInExpoWebView,
    prepareCameraInput,
} from 'utils/nativeCamera'

let expoCameraPermissionReady = false

/**
 * @typedef {Object} AddImageButtonProps
 * @property {boolean} [answerHover]
 * @property {string} btnId
 * @property {any} [answer]
 * @property {any} [quest]
 * @property {(ev: React.ChangeEvent<HTMLInputElement>) => void} imgHandler
 * @property {(ev: React.ChangeEvent<HTMLInputElement>) => void} videoHandler
 * @property {React.Ref<HTMLInputElement>} [videoRef]
 */

/** @type {React.ForwardRefExoticComponent<AddImageButtonProps & React.RefAttributes<HTMLInputElement>>} */
const AddImageButton = React.forwardRef((props, ref) => {
    const [showMenu, setShowMenu] = useState(false)
    const [preparingCamera, setPreparingCamera] = useState(false)
    const triggerRef = useRef(null)
    const menuRef = useRef(null)
    const answerHoverStyles = props.answerHover ? styles.hoverBtn : null
    const mobileStyles = isMobile ? styles.mobileBtn : null
    const photoInputId = `answer-img-file-input-${props.btnId}`
    const videoInputId = `answer-video-file-input-${props.btnId}`
    const menuId = `answer-camera-menu-${props.btnId}`
    const videoCapture =
        !isRunningInExpoWebView() || hasNativeVideoCaptureInput() ? 'environment' : undefined

    useEffect(() => {
        if (!showMenu) return

        const closeOnOutsidePress = ev => {
            if (
                menuRef.current &&
                !menuRef.current.contains(ev.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(ev.target)
            ) {
                setShowMenu(false)
            }
        }
        const closeOnEscape = ev => {
            if (ev.key === 'Escape') {
                setShowMenu(false)
                triggerRef.current?.focus()
            }
        }

        document.addEventListener('mousedown', closeOnOutsidePress)
        document.addEventListener('touchstart', closeOnOutsidePress)
        document.addEventListener('keydown', closeOnEscape)
        return () => {
            document.removeEventListener('mousedown', closeOnOutsidePress)
            document.removeEventListener('touchstart', closeOnOutsidePress)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [showMenu])

    const toggleCameraMenu = async () => {
        if (showMenu) {
            setShowMenu(false)
            return
        }

        if (!hasNativeCameraPermissionGate() || expoCameraPermissionReady) {
            setShowMenu(true)
            return
        }

        setPreparingCamera(true)
        try {
            expoCameraPermissionReady = await prepareCameraInput()
            if (!expoCameraPermissionReady) {
                alert(
                    'Camera permission is required to capture media. You can still use the image picker.',
                )
                return
            }
            setShowMenu(true)
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            alert(`Unable to open camera: ${message}`)
        } finally {
            setPreparingCamera(false)
        }
    }

    const openCaptureFromKeyboard = (ev, inputId) => {
        if (ev.key !== 'Enter' && ev.key !== ' ') return

        ev.preventDefault()
        document.getElementById(inputId)?.click()
    }

    const rect = triggerRef.current?.getBoundingClientRect()
    const menuWidth = 160
    const menuHeight = 92
    const viewportPadding = 8
    const menuGap = 6
    const menuStyle = rect
        ? {
              position: 'fixed',
              left: Math.min(
                  Math.max(viewportPadding, rect.left),
                  window.innerWidth - menuWidth - viewportPadding,
              ),
              ...(rect.bottom + menuGap + menuHeight <= window.innerHeight - viewportPadding
                  ? { top: rect.bottom + menuGap }
                  : { bottom: window.innerHeight - rect.top + menuGap }),
          }
        : {}

    return (
        <div
            className={cn(
                styles.editMediaBtn,
                answerHoverStyles,
                styles.imgHover,
                styles.addImgBtn,
                mobileStyles,
            )}
            onClick={ev => ev.stopPropagation()}
        >
            <WithTooltip asChild tip="Open camera.">
                <button
                    ref={triggerRef}
                    type="button"
                    className={styles.cameraCaptureTrigger}
                    onClick={toggleCameraMenu}
                    disabled={preparingCamera}
                    aria-label="Open camera options"
                    aria-haspopup="menu"
                    aria-expanded={showMenu}
                    aria-controls={menuId}
                    aria-busy={preparingCamera}
                >
                    <Camera
                        id={`answer-image-btn`}
                        className="image-upload-control"
                        aria-hidden="true"
                    />
                </button>
            </WithTooltip>

            {showMenu &&
                ReactDOM.createPortal(
                    <div
                        id={menuId}
                        ref={menuRef}
                        className={styles.cameraCaptureMenu}
                        style={menuStyle}
                        role="menu"
                        aria-label="Camera options"
                        onMouseDown={ev => ev.stopPropagation()}
                        onTouchStart={ev => ev.stopPropagation()}
                        onClick={ev => ev.stopPropagation()}
                    >
                        <label
                            htmlFor={photoInputId}
                            className={styles.cameraCaptureMenuItem}
                            data-camera-mode="photo"
                            role="menuitem"
                            tabIndex={0}
                            onKeyDown={ev => openCaptureFromKeyboard(ev, photoInputId)}
                        >
                            <Camera aria-hidden="true" />
                            <span>Photo</span>
                            <input
                                style={{
                                    height: 0,
                                    top: 30000,
                                    left: 30000,
                                    position: 'absolute',
                                    display: 'none',
                                    width: 0,
                                }}
                                id={photoInputId}
                                ref={ref}
                                className="answer-img"
                                name="answer_image"
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                data-answer-id={props.answer?.id}
                                onChange={ev => {
                                    window.logEv('CLICK_ATTACH_IMAGE_BUTTON', {
                                        answer_id: props.answer?.id,
                                        quest_id: props.quest?.id || props.answer?.quest_id,
                                        from_location: 'answer_controls',
                                        via: 'camera_menu',
                                    })
                                    props.imgHandler(ev)
                                    setShowMenu(false)
                                }}
                            />
                        </label>
                        <label
                            htmlFor={videoInputId}
                            className={styles.cameraCaptureMenuItem}
                            data-camera-mode="video"
                            role="menuitem"
                            tabIndex={0}
                            onKeyDown={ev => openCaptureFromKeyboard(ev, videoInputId)}
                        >
                            <Video aria-hidden="true" />
                            <span>Video</span>
                            <input
                                style={{
                                    height: 0,
                                    top: 30000,
                                    left: 30000,
                                    position: 'absolute',
                                    display: 'none',
                                    width: 0,
                                }}
                                id={videoInputId}
                                ref={props.videoRef}
                                className="new-answer-video-input"
                                name="answer_video"
                                type="file"
                                accept="video/*"
                                capture={videoCapture}
                                data-answer-id={props.answer?.id}
                                onChange={ev => {
                                    props.videoHandler(ev)
                                    setShowMenu(false)
                                }}
                            />
                        </label>
                    </div>,
                    document.body,
                )}
        </div>
    )
})

export default AddImageButton
